import React, { useCallback, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { compareNumber } from 'src/utils/miscUtils';
import {
  AutoPricingConstraintType,
  AutoPricingResultType,
  BulkListingPriceCalculation,
  ListingNote,
  ListingPriceCalculation,
} from 'src/WebApiController';

import {
  ComparableListingItem,
  ComparableListingItemProps,
  DisplayType,
} from './ComparableListingItem';
import * as styles from './ComparableListings.css';

const calculateDealScore = (
  sellerNetProceeds: number | undefined | null,
  seatScore: number | undefined | null
) => {
  if (sellerNetProceeds == null || !seatScore) {
    return undefined;
  }

  return sellerNetProceeds / seatScore;
};

export type ComparableListing = {
  listingId: number;
  section?: string;
  row?: string;
  seatFr?: string;
  seatTo?: string;
  price?: number | null;
  priceDisp?: string | null;
  unitCost?: number | null;
  sellerNetProceeds?: number;
  currencyCode?: string;
  listingNotes?: ListingNote[];
  isYourListing?: boolean; // Seller selected listing
  isSellerListing?: boolean; // Seller listing
  isListed?: boolean; // Flag to tell if seller listing is broadcast or not
  isOutlier?: boolean;
  isAnchor?: boolean;
  isFanInventory?: boolean;
  seatScore?: number | null;
  availableTickets?: number | null;
  validPurchaseQuantities?: number[] | null;
  isSelectedWithListingGroup?: boolean;
  autoPricingResultType?: AutoPricingResultType;
  autoPricingResultConstraintType?: AutoPricingConstraintType;
  shouldShowResultTypeAction?: boolean;
  officeId?: number | null;
};

export type ComparableListingsProps = {
  currentListings?: ComparableListing[];
  comparableListings: ComparableListing[];
  sortBy?: 'price' | 'dealScore';
  showOnlyMyListings?: boolean;
  isSortDescending?: boolean;
  isExcludingFanInventory?: boolean;
  floorPrice?: number | null;
  ceilingPrice?: number | null;
  listingPriceCalc?: ListingPriceCalculation | null;
  listingPriceCalculations?: BulkListingPriceCalculation[] | null;
};

export const ComparableListings: React.FC<ComparableListingsProps> = ({
  currentListings,
  comparableListings,
  sortBy,
  showOnlyMyListings,
  isSortDescending,
  floorPrice,
  ceilingPrice,
  listingPriceCalc,
  listingPriceCalculations,
  isExcludingFanInventory,
}) => {
  const { getUiCurrency } = useLocalizationContext();

  const firstCurrentListing = currentListings?.[0];
  const currentListingIds = useMemo(() => {
    return currentListings?.map((listing) => listing.listingId);
  }, [currentListings]);

  const floorPriceUICurrency = getUiCurrency(firstCurrentListing?.currencyCode);

  const addFloorCeilingLine = useCallback(
    (
      items: ComparableListingItemProps[],
      type: DisplayType.FloorDisplay | DisplayType.CeilingDisplay,
      price: number
    ) => {
      items.push({
        displayType: type,
        itemProps: {
          displayType: type,
          floorCeilingPrice: price,
          currency: floorPriceUICurrency,
        },
      });

      return true;
    },
    [floorPriceUICurrency]
  );

  const allListings = useMemo(() => {
    const all: ComparableListing[] = [];
    if (currentListings) {
      all.push(...currentListings);
    }
    all.push(...comparableListings);

    return all.toSorted((a, b) => {
      let cmpRes = 0;
      if (sortBy === 'dealScore') {
        const dealScoreA = calculateDealScore(a.sellerNetProceeds, a.seatScore);
        const dealScoreB = calculateDealScore(b.sellerNetProceeds, b.seatScore);
        cmpRes = compareNumber(dealScoreA, dealScoreB);
      } else if (sortBy === 'price') {
        cmpRes = compareNumber(a.price, b.price);
      }
      return cmpRes * (isSortDescending ? -1 : 1);
    });
  }, [comparableListings, currentListings, sortBy, isSortDescending]);

  const listingItems = useMemo(() => {
    const items: ComparableListingItemProps[] = [];
    let floorPriceShown = false;
    let ceilingPriceShown = false;

    // Determine if all listings are above floor/ceiling
    if (sortBy === 'price') {
      if (floorPrice != null) {
        const anyPriceLessThanFloor = allListings.some(
          (l) => l.price != null && l.price < floorPrice
        );
        if (!anyPriceLessThanFloor) {
          // If there are no listing less than floor, show floor first
          floorPriceShown = addFloorCeilingLine(
            items,
            DisplayType.FloorDisplay,
            floorPrice
          );
        }
      }
      if (ceilingPrice != null) {
        const anyPriceLessThanCeiling = allListings.some(
          (l) => l.price != null && l.price < ceilingPrice
        );
        if (!anyPriceLessThanCeiling) {
          // If there are no listing less than ceiling, show ceiling first
          ceilingPriceShown = addFloorCeilingLine(
            items,
            DisplayType.CeilingDisplay,
            ceilingPrice
          );
        }
      }
    }

    allListings.forEach((listing) => {
      if (
        sortBy === 'price' &&
        !floorPriceShown &&
        floorPrice != null &&
        listing.price != null
      ) {
        if (
          (isSortDescending && listing.price < floorPrice) ||
          (!isSortDescending && listing.price >= floorPrice)
        ) {
          floorPriceShown = addFloorCeilingLine(
            items,
            DisplayType.FloorDisplay,
            floorPrice
          );
        }
      }

      if (
        sortBy === 'price' &&
        !ceilingPriceShown &&
        ceilingPrice != null &&
        listing.price != null
      ) {
        if (
          (isSortDescending && listing.price < ceilingPrice) ||
          (!isSortDescending && listing.price >= ceilingPrice)
        ) {
          ceilingPriceShown = addFloorCeilingLine(
            items,
            DisplayType.CeilingDisplay,
            ceilingPrice
          );
        }
      }

      if (
        showOnlyMyListings &&
        !currentListingIds?.includes(listing.listingId)
      ) {
        return;
      }

      if (listing.listingId === firstCurrentListing?.listingId) {
        items.push({
          displayType: DisplayType.AutopricerDisplay,
          itemProps: {
            isCurrentListing: currentListingIds?.includes(listing.listingId),
            shouldShowResultTypeAction:
              firstCurrentListing!.shouldShowResultTypeAction,
            currentListing: firstCurrentListing!,
            listingPriceCalc,
            listingPriceCalculations,
            comparableListings,
            isExcludingFanInventory,
          },
        });
      } else {
        items.push({
          displayType: DisplayType.ComparableDisplay,
          itemProps: {
            isCurrentListing: currentListingIds?.includes(listing.listingId),
            listing,
            listingPriceCalculations,
            comparableListings,
            isExcludingFanInventory,
          },
        });
      }
    });

    // Determine if all listings are below floor/ceiling
    if (sortBy === 'price') {
      if (floorPrice != null && !floorPriceShown) {
        // If there are no listing greater than floor, show floor last
        addFloorCeilingLine(items, DisplayType.FloorDisplay, floorPrice);
      }
      if (ceilingPrice != null && !ceilingPriceShown) {
        // If there are no listing greater than ceiling, show ceiling last
        addFloorCeilingLine(items, DisplayType.CeilingDisplay, ceilingPrice);
      }
    }

    return items;
  }, [
    addFloorCeilingLine,
    allListings,
    ceilingPrice,
    comparableListings,
    currentListingIds,
    firstCurrentListing,
    floorPrice,
    isSortDescending,
    listingPriceCalc,
    listingPriceCalculations,
    showOnlyMyListings,
    sortBy,
    isExcludingFanInventory,
  ]);

  return (
    <div className={styles.root}>
      <div className={styles.comparableListings}>
        <Virtuoso
          className={styles.virtuosoListings}
          data={listingItems}
          itemContent={(_, item) => <ComparableListingItem {...item} />}
        />
      </div>
    </div>
  );
};
